import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLicense } from '../context/LicenseContext';
import axios from 'axios';
import API_BASE from '../utils/api';
import getProfilePictureSrc from '../utils/profilePicture';

const Sidebar = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const { isModuleEnabled } = useLicense();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [schoolName, setSchoolName] = useState('');
  const [schoolLogo, setSchoolLogo] = useState('');
  // Admin-configured module permissions for this user's role (teacher/
  // parent only — see backend/routes/rolePermissions.js). null means "not
  // applicable" (other roles) or "not loaded yet", in which case nothing
  // gets hidden — avoids a flash of a missing menu before the fetch resolves.
  const [rolePermissionModules, setRolePermissionModules] = useState(null);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    axios.get(`${API_BASE}/settings/public`)
      .then(res => {
        if (res.data.schoolName) {
          setSchoolName(res.data.schoolName);
          document.title = res.data.schoolName;
        }
        if (res.data.logo) setSchoolLogo(res.data.logo);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role !== 'class_teacher' && user?.role !== 'parent') {
      setRolePermissionModules(null);
      return;
    }
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/role-permissions/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setRolePermissionModules(res.data.modules))
      .catch(() => setRolePermissionModules(null));
  }, [user?.role]);

  // A module absent from the permissions map isn't gated by this system at
  // all (e.g. "classes"/"My Class") — only modules explicitly listed are
  // subject to the admin's read-access toggle.
  const isPermissionAllowed = (moduleKey) => {
    if (!moduleKey || !rolePermissionModules) return true;
    const actions = rolePermissionModules[moduleKey];
    if (actions === undefined) return true;
    return actions.includes('read');
  };

const adminMenu = [
  { path: '/admin/dashboard', name: 'Dashboard', icon: '📊' },
  { path: '/admin/admission', name: 'Admission Form', icon: '📝' },
  { path: '/admin/applications', name: 'Applications', icon: '📮' },
  { path: '/admin/students', name: 'Students', icon: '👥' },
  { path: '/admin/promotions', name: 'Promotions', icon: '🎓' },
  { path: '/admin/custom-fields', name: 'Custom Fields', icon: '🧩' },
  { path: '/admin/teachers', name: 'Teachers', icon: '👨‍🏫' },
  { path: '/admin/classes', name: 'Classes', icon: '🏫', module: 'classes' },
  { path: '/admin/transport', name: 'Transport', icon: '🚌', module: 'transport' },
  { path: '/admin/fees', name: 'Fees', icon: '💰', module: 'fees' },
  { path: '/admin/fee-structures', name: 'Fee Structures', icon: '🧮', module: 'fees' },
  { path: '/admin/discount-definitions', name: 'Discounts', icon: '🎓', module: 'fees' },
  { path: '/admin/salary', name: 'Salary', icon: '💵', module: 'salary' },
  { path: '/admin/non-teaching-staff', name: 'Non-Teaching Staff', icon: '🧑‍🔧', module: 'nonTeachingStaff' },
  { path: '/admin/attendance', name: 'Attendance', icon: '📋', module: 'attendance' },
  { path: '/admin/leaves', name: 'Leaves', icon: '📅', module: 'leaves' },
  { path: '/admin/exams', name: 'Exams', icon: '📝', module: 'exams' },
  { path: '/admin/progress-reports', name: 'Progress Reports', icon: '📈', module: 'progressReports' },
  { path: '/admin/expenses', name: 'Expenses', icon: '🧾', module: 'expenses' },
  { path: '/admin/communications', name: 'Communications', icon: '📱', module: 'communications' },
  { path: '/admin/reports', name: 'Reports', icon: '📈' },
  { path: '/admin/holidays', name: 'Holiday Calendar', icon: '🗓️' },
  { path: '/admin/users', name: 'Users', icon: '👥' },
  { path: '/admin/role-permissions', name: 'Role Permissions', icon: '🔑' },
  { path: '/admin/activity-log', name: 'Activity Log', icon: '📜' },
  { path: '/chat', name: 'Chat', icon: '💬' },
  { path: '/admin/settings', name: 'Settings', icon: '⚙️' },
];

const superAdminMenu = [
  { path: '/super-admin/dashboard', name: 'Dashboard', icon: '📊' },
  { path: '/super-admin/profile', name: 'My Profile', icon: '👤' },
  { path: '/super-admin/schools', name: 'Schools', icon: '🏫' },
  { path: '/super-admin/license', name: 'License', icon: '🔐' },
  { path: '/super-admin/admins', name: 'Admins', icon: '👨‍💼' },
  { path: '/super-admin/sms-config', name: 'SMS Integration', icon: '📱' },
  { path: '/super-admin/email-config', name: 'Email Config', icon: '📧' },
  { path: '/super-admin/payment-config', name: 'Payment Gateway', icon: '💳' },
  { path: '/super-admin/attendance-device-config', name: 'Attendance Devices', icon: '📹' },
  { path: '/admin/students', name: 'Students', icon: '👥' },
  { path: '/admin/promotions', name: 'Promotions', icon: '🎓' },
  { path: '/admin/custom-fields', name: 'Custom Fields', icon: '🧩' },
  { path: '/admin/teachers', name: 'Teachers', icon: '👨‍🏫' },
  { path: '/admin/classes', name: 'Classes', icon: '🏫' },
  { path: '/principal/teachers', name: 'Principal Management', icon: '📚' },
  { path: '/admin/transport', name: 'Transport', icon: '🚌' },
  { path: '/admin/fees', name: 'Fees', icon: '💰' },
  { path: '/admin/fee-structures', name: 'Fee Structures', icon: '🧮' },
  { path: '/admin/discount-definitions', name: 'Discounts', icon: '🎓' },
  { path: '/admin/salary', name: 'Salary', icon: '💵' },
  { path: '/admin/non-teaching-staff', name: 'Non-Teaching Staff', icon: '🧑‍🔧' },
  { path: '/admin/attendance', name: 'Attendance', icon: '📋' },
  { path: '/admin/leaves', name: 'Leaves', icon: '📅' },
  { path: '/admin/exams', name: 'Exams', icon: '📝' },
  { path: '/admin/progress-reports', name: 'Progress Reports', icon: '📈' },
  { path: '/admin/expenses', name: 'Expenses', icon: '🧾' },
  { path: '/admin/communications', name: 'Communications', icon: '📱' },
  { path: '/admin/reports', name: 'Reports', icon: '📈' },
  { path: '/admin/users', name: 'All Users', icon: '👥' },
  { path: '/admin/role-permissions', name: 'Role Permissions', icon: '🔑' },
  { path: '/admin/settings', name: 'Settings', icon: '⚙️' },
];

  const teacherMenu = [
    { path: '/teacher/dashboard', name: 'Dashboard', icon: '📊' },
    { path: '/teacher/selfie-attendance', name: 'My Attendance', icon: '📸', module: 'attendance' },
    { path: '/teacher/attendance', name: 'Student Attendance', icon: '📋', module: 'attendance' },
    { path: '/teacher/class', name: 'My Class', icon: '🏫', module: 'classes' },
    { path: '/teacher/students', name: 'Students', icon: '👥' },
    { path: '/teacher/leaves', name: 'Leaves', icon: '📅', module: 'leaves' },
    { path: '/teacher/subjects', name: 'Subjects', icon: '📚', module: 'classes' },
    { path: '/teacher/daily-modules', name: 'Daily Modules', icon: '📝', module: 'dailyModules' },
    { path: '/teacher/exams', name: 'Exams & Marks', icon: '📄', module: 'exams' },
    { path: '/teacher/progress-reports', name: 'Progress Reports', icon: '📈', module: 'progressReports' },
    { path: '/teacher/timetable', name: 'Timetable', icon: '📅', module: 'timetable' },
    { path: '/teacher/materials', name: 'Books & Uniform', icon: '📚', module: 'materials' },
    { path: '/teacher/events', name: 'Events', icon: '🎉', module: 'events' },
    { path: '/teacher/holidays', name: 'Holiday Calendar', icon: '🗓️' },
    { path: '/chat', name: 'Chat', icon: '💬' },
  ];

  const parentMenu = [
    { path: '/parent/dashboard', name: 'Dashboard', icon: '📊' },
    { path: '/parent/child', name: 'My Child', icon: '👶' },
    { path: '/parent/attendance', name: 'Attendance', icon: '📋', module: 'attendance' },
    { path: '/parent/fees', name: 'Fees', icon: '💰', module: 'fees' },
    { path: '/parent/transport', name: 'Transport', icon: '🚌', module: 'transport' },
    { path: '/parent/daily-modules', name: 'Daily Modules', icon: '📝', module: 'dailyModules' },
    { path: '/parent/exams', name: 'Exam Results', icon: '📄', module: 'exams' },
    { path: '/parent/progress-reports', name: 'Progress Reports', icon: '📈', module: 'progressReports' },
    { path: '/parent/notifications', name: 'Notifications', icon: '🔔' },
    { path: '/parent/timetable', name: 'Timetable', icon: '📅', module: 'timetable' },
    { path: '/parent/materials', name: 'Books & Uniform', icon: '📚', module: 'materials' },
    { path: '/parent/events', name: 'Events', icon: '🎉', module: 'events' },
  ];

  const principalMenu = [
    { path: '/principal/dashboard', name: 'Dashboard', icon: '📊' },
    { path: '/principal/admission', name: 'Admission Form', icon: '📝' },
    { path: '/principal/applications', name: 'Applications', icon: '📮' },
    { path: '/principal/teachers', name: 'Teachers', icon: '👨‍🏫' },
    { path: '/principal/students', name: 'Students', icon: '👥' },
    { path: '/admin/promotions', name: 'Promotions', icon: '🎓' },
    { path: '/principal/non-teaching-staff', name: 'Non-Teaching Staff', icon: '🧑‍🔧', module: 'nonTeachingStaff' },
    { path: '/principal/attendance', name: 'Attendance', icon: '📋', module: 'attendance' },
    { path: '/principal/leaves', name: 'Leaves', icon: '📅', module: 'leaves' },
    { path: '/principal/daily-modules', name: 'Daily Modules', icon: '📝', module: 'dailyModules' },
    { path: '/principal/subjects', name: 'Subjects', icon: '📚', module: 'classes' },
    { path: '/principal/fees', name: 'Fees', icon: '💰', module: 'fees' },
    { path: '/admin/fee-structures', name: 'Fee Structures', icon: '🧮', module: 'fees' },
    { path: '/admin/discount-definitions', name: 'Discounts', icon: '🎓', module: 'fees' },
    { path: '/principal/expenses', name: 'Expenses', icon: '🧾', module: 'expenses' },
    { path: '/principal/exams', name: 'Exam Review', icon: '📄', module: 'exams' },
    { path: '/principal/progress-reports', name: 'Progress Reports', icon: '📈', module: 'progressReports' },
    { path: '/principal/timetable', name: 'Timetable', icon: '📅', module: 'timetable' },
    { path: '/principal/materials', name: 'Books & Uniform', icon: '📚', module: 'materials' },
    { path: '/principal/events', name: 'Events', icon: '🎉', module: 'events' },
    { path: '/admin/holidays', name: 'Holiday Calendar', icon: '🗓️' },
    { path: '/admin/activity-log', name: 'Activity Log', icon: '📜' },
    { path: '/chat', name: 'Chat', icon: '💬' },
    { path: '/principal/settings', name: 'Settings', icon: '⚙️' },
  ];

  let rawMenu = [];
  switch(user?.role) {
    case 'super_admin':
      rawMenu = superAdminMenu;
      break;
    case 'admin':
      rawMenu = adminMenu;
      break;
    case 'principal':
      rawMenu = principalMenu;
      break;
    case 'class_teacher':
      rawMenu = teacherMenu;
      break;
    case 'parent':
      rawMenu = parentMenu;
      break;
    default:
      rawMenu = [];
  }

  const menuItems = rawMenu.filter(item => isModuleEnabled(item.module) && isPermissionAllowed(item.module));

  return (
    <>
      {/* Hamburger button - mobile only, fixed position */}
      <button
        className="lg:hidden fixed top-3 left-3 z-40 text-white p-2 rounded-lg shadow-lg"
        onClick={() => setIsOpen(true)}
        style={{ display: isOpen ? 'none' : 'block', backgroundColor: theme.sidebarColor }}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 text-white h-screen overflow-y-auto
        transform transition-transform duration-300 ease-in-out shrink-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} style={{ backgroundColor: theme.sidebarColor }}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-6">
            <div 
              className="flex items-center gap-3 cursor-pointer hover:opacity-80"
              onClick={() => navigate('/')}
            >
              <img src={schoolLogo || "/logo.jpeg"} alt="Logo" className="w-10 h-10 rounded-full object-cover border-2" style={{ borderColor: theme.accentColor }} />
              <h2 className="text-lg font-bold leading-tight">{schoolName || 'School MS'}</h2>
            </div>
            <button
              className="lg:hidden text-white text-2xl p-1"
              onClick={() => setIsOpen(false)}
            >
              ✕
            </button>
          </div>

          {/* User Profile Section */}
          {user && (
            <div 
              className="mb-6 p-3 rounded-lg cursor-pointer hover:opacity-90 transition"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              onClick={() => {
                const profileRole = user.role === 'super_admin' ? 'super-admin' : user.role;
                navigate(`/${profileRole === "class_teacher" ? "teacher" : profileRole}/profile`);                
                setIsOpen(false);
              }}
            >
              <div className="flex items-center gap-3">
                {user.profilePicture ? (
                  <img
                    src={getProfilePictureSrc(user.profilePicture)}
                    alt={user.name}
                    className="w-10 h-10 rounded-full object-cover border-2"
                    style={{ borderColor: theme.accentColor }}
                  />
                ) : (
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                  >
                    {user.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{user.name}</p>
                  <p className="text-xs opacity-75 truncate">
                    {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Administrator' : user.role === 'principal' ? 'Principal' : user.role === 'class_teacher' ? 'Class Teacher' : 'Parent'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <nav>
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center space-x-3 p-3 rounded-lg mb-2 transition-colors`
                }
                style={({ isActive }) => ({
                  backgroundColor: isActive ? 'var(--sidebar-active)' : 'transparent',
                })}
                onMouseEnter={(e) => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.backgroundColor = 'var(--sidebar-hover)'; }}
                onMouseLeave={(e) => { const isActive = e.currentTarget.getAttribute('aria-current'); if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span className="text-xl">{item.icon}</span>
                <span>{item.name}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;